<template>
  <section class="space-y-4">
    <div class="flex flex-wrap items-end justify-between gap-3">
      <div>
        <h2 class="text-lg font-semibold text-zinc-900">Broadcast engagement</h2>
        <p class="mt-1 text-sm text-zinc-500">Delivery for in-app and SMS Text Message broadcasts, with in-app opens and tracked link clicks across both channels.</p>
      </div>
      <div class="flex items-center gap-3">
        <PlatformaSelect v-model="range" :options="rangeOptions" class="min-w-[180px]" />
        <button type="button" class="h-10 rounded-lg bg-zinc-100 px-4 text-sm font-semibold text-zinc-700 transition hover:bg-zinc-200 disabled:opacity-50" :disabled="loading" @click="loadAnalytics">{{ loading ? 'Loading…' : 'Refresh' }}</button>
      </div>
    </div>

    <div v-if="error" class="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{{ error }}</div>

    <div class="grid grid-cols-2 gap-3 xl:grid-cols-6">
      <div v-for="metric in metrics" :key="metric.label" class="rounded-xl border border-zinc-200 bg-white p-4 shadow-sm">
        <p class="text-xs font-medium uppercase tracking-wide text-zinc-500">{{ metric.label }}</p>
        <p class="mt-1 text-2xl font-semibold text-zinc-900">{{ metric.value }}</p>
      </div>
    </div>

    <div class="overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-sm">
      <div class="border-b border-zinc-200 px-4 py-3">
        <h3 class="text-sm font-semibold text-zinc-900">Sent broadcasts</h3>
      </div>
      <div v-if="loading && !rows.length" class="px-4 py-12 text-center text-sm text-zinc-500">Loading broadcast engagement…</div>
      <div v-else-if="!rows.length" class="px-4 py-12 text-center text-sm text-zinc-500">No sent broadcasts in this range.</div>
      <div v-else class="overflow-x-auto">
        <table class="min-w-full text-sm">
          <thead class="bg-zinc-50 text-zinc-500">
            <tr>
              <th class="px-4 py-3 text-left font-medium">Broadcast</th>
              <th class="px-4 py-3 text-left font-medium">Sent</th>
              <th class="px-4 py-3 text-right font-medium">Delivered</th>
              <th class="px-4 py-3 text-right font-medium" title="SMS providers do not expose a reliable message-open event.">Opened</th>
              <th class="px-4 py-3 text-right font-medium">Clicked</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-zinc-100">
            <tr v-for="row in rows" :key="row.slug" class="hover:bg-zinc-50/70">
              <td class="px-4 py-3 font-medium text-zinc-900"><span>{{ row.name || 'Untitled broadcast' }}</span><span class="ml-2 rounded-full bg-zinc-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-zinc-500">{{ row.channel === 'sms' ? 'SMS Text' : 'In-App' }}</span></td>
              <td class="whitespace-nowrap px-4 py-3 text-zinc-500">{{ formatDate(row.sentAt) }}</td>
              <td class="px-4 py-3 text-right text-zinc-700">{{ row.deliveredCount }}</td>
              <td class="px-4 py-3 text-right"><template v-if="row.channel !== 'sms'"><span class="font-semibold text-zinc-900">{{ row.openedCount }}</span><span class="ml-1 text-xs text-zinc-400">({{ row.openRate }}%)</span></template><span v-else class="text-xs font-medium text-zinc-400" title="SMS providers do not expose a reliable message-open event.">N/A</span></td>
              <td class="px-4 py-3 text-right"><span class="font-semibold text-zinc-900">{{ row.clickedCount }}</span><span class="ml-1 text-xs text-zinc-400">({{ row.clickRate }}%)</span></td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import PlatformaSelect from '@/Components/PlatformaSelect.vue'
import { useWebsocketStore } from '@/stores/websocketStore'

const props = defineProps({ active: { type: Boolean, default: false } })
const apiProxyUrl = String(process.env.VUE_APP_API_PROXY_URL || '').replace(/\/+$/, '')
const rangeOptions = [
  { value: '7', label: 'Last 7 days' },
  { value: '30', label: 'Last 30 days' },
  { value: '90', label: 'Last 90 days' },
  { value: '365', label: 'Last 365 days' },
  { value: 'lifetime', label: 'Lifetime' },
]
const range = ref('30')
const loading = ref(false)
const error = ref('')
const data = ref({ summary: {}, broadcasts: [] })
const websocketStore = useWebsocketStore()
let unsubscribe = null
let refreshTimer = null

const rows = computed(() => Array.isArray(data.value?.broadcasts) ? data.value.broadcasts : [])
const metrics = computed(() => {
  const summary = data.value?.summary || {}
  return [
    { label: 'Recipients', value: Number(summary.totalTargets || 0).toLocaleString() },
    { label: 'Delivered', value: Number(summary.deliveredCount || 0).toLocaleString() },
    { label: 'Unique opens (in-app)', value: Number(summary.openedCount || 0).toLocaleString() },
    { label: 'Open rate (in-app)', value: `${Number(summary.openRate || 0)}%` },
    { label: 'Unique clicks', value: Number(summary.clickedCount || 0).toLocaleString() },
    { label: 'Click rate', value: `${Number(summary.clickRate || 0)}%` },
  ]
})

async function loadAnalytics() {
  if (!props.active) return
  loading.value = true
  error.value = ''
  try {
    const response = await fetch(`${apiProxyUrl}/api/creator-broadcasts/analytics?days=${encodeURIComponent(range.value)}`, { credentials: 'include', cache: 'no-store' })
    const payload = await response.json().catch(() => ({}))
    if (!response.ok || payload.success === false) throw new Error(payload.message || 'Failed to load broadcast analytics.')
    data.value = payload
  } catch (loadError) {
    error.value = loadError?.message || 'Failed to load broadcast analytics.'
  } finally {
    loading.value = false
  }
}
function scheduleRefresh() {
  if (!props.active) return
  if (refreshTimer) clearTimeout(refreshTimer)
  refreshTimer = setTimeout(loadAnalytics, 250)
}
function formatDate(value) {
  const date = value ? new Date(value) : null
  return date && !Number.isNaN(date.getTime())
    ? new Intl.DateTimeFormat('en-US', { dateStyle: 'medium', timeStyle: 'short' }).format(date)
    : '—'
}

watch(() => props.active, (active) => {
  if (!active) return
  void websocketStore.connect()
  if (!unsubscribe) unsubscribe = websocketStore.subscribe('creator-broadcast:changed', scheduleRefresh)
  void loadAnalytics()
}, { immediate: true })
watch(range, loadAnalytics)
onBeforeUnmount(() => {
  if (refreshTimer) clearTimeout(refreshTimer)
  if (unsubscribe) unsubscribe()
})
</script>
